import { Button, Fade, HStack, IconButton, Tooltip } from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useTableBoxContext } from "shared/components/table/box/TableBoxContext";
import { CollapseIcon } from "shared/icons/CollapseIcon";
import { ExpandIcon } from "shared/icons/ExpandIcon";

export interface TableBoxActionsProps {
  showFullScreen?: boolean;
  endChildren?: React.ReactNode;
}

export const TableBoxActions: React.FC<
  React.PropsWithChildren<TableBoxActionsProps>
> = ({ showFullScreen = true, children, endChildren }) => {
  const { isCollapsible, isExpanded } = useTableBoxContext();

  const content = (
    <HStack flex={1} justifyContent="flex-end" color="gray.500">
      {children}
      {showFullScreen && <TableBoxFullScreenButton />}
      {endChildren}
    </HStack>
  );

  if (!isCollapsible) {
    return content;
  }

  return (
    <Fade
      initial={isExpanded}
      in={isExpanded}
      style={{
        display: "flex",
        flex: 1,
        justifyContent: "flex-end",
      }}
      transition={{
        enter: {
          display: "flex",
        },
      }}
      transitionEnd={{
        exit: {
          display: "none",
        },
      }}
    >
      {content}
    </Fade>
  );
};

const TableBoxFullScreenButton: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const { isFullScreen, setFullScreen } = useTableBoxContext();

  if (isFullScreen) {
    return (
      <Button
        size="xs"
        colorScheme="tertiary"
        leftIcon={<CollapseIcon />}
        onClick={() => setFullScreen(false)}
      >
        {t("common:table.collapse")}
      </Button>
    );
  }

  const label = t("common:table.openFullscreen");
  return (
    <Tooltip label={label}>
      <IconButton
        variant="ghost"
        size="tablebox-icon"
        aria-label={label}
        icon={<ExpandIcon />}
        onClick={() => setFullScreen(true)}
      />
    </Tooltip>
  );
};
